import type { LayoutElement } from '../../../../types';
import { portEdge, type Port, type PortEdge } from '../../../../utils/ports';

export type Equipment3DPortFacing = {
  edge: PortEdge;
  rotationY: number;
  /** Left/right-facing kits rotate 90°, so their local width/depth must swap. */
  swapsFootprintAxes: boolean;
};

const FACING_BY_EDGE: Record<PortEdge, Equipment3DPortFacing> = {
  bottom: { edge: 'bottom', rotationY: 0, swapsFootprintAxes: false },
  right: { edge: 'right', rotationY: Math.PI / 2, swapsFootprintAxes: true },
  top: { edge: 'top', rotationY: Math.PI, swapsFootprintAxes: false },
  left: { edge: 'left', rotationY: -Math.PI / 2, swapsFootprintAxes: true },
};

/**
 * Resolve the procedural kit's front (+Z) from the load-port edge shown in 2D.
 * The outer layout-element rotation is applied separately by the 3D canvas.
 */
export function resolveEquipment3DPortFacing(
  element?: Pick<LayoutElement, 'customProperties'> | null
): Equipment3DPortFacing {
  const stored = element?.customProperties?.ports;
  if (!Array.isArray(stored) || stored.length === 0) return FACING_BY_EDGE.bottom;

  const counts: Record<PortEdge, number> = { top: 0, right: 0, bottom: 0, left: 0 };
  for (const port of stored as Port[]) counts[portEdge(port)] += 1;

  // Preserve the first port's edge on a tie; equipment load ports normally
  // share one face, while this keeps old mixed-port layouts deterministic.
  const firstEdge = portEdge(stored[0] as Port);
  const edge = (Object.keys(counts) as PortEdge[]).reduce(
    (best, candidate) => (counts[candidate] > counts[best] ? candidate : best),
    firstEdge
  );
  return FACING_BY_EDGE[edge];
}

/**
 * Convert a canvas/world point into the procedural equipment kit's local
 * coordinates. The outer layout rotation and the inner port-facing rotation
 * are both inverted, so a rail node remains on its real left/right side.
 */
export function worldPointToEquipmentKitLocal(
  element: Pick<LayoutElement, 'position' | 'rotation' | 'dimensions' | 'customProperties'>,
  point: { x: number; y: number },
  localY: number
): [number, number, number] {
  const rotation = (element.rotation * Math.PI) / 180;
  const dx = point.x - element.position.x;
  const dz = point.y - element.position.y;

  // FactoryElement3D applies -layoutRotation around the element's top-left.
  const layoutX = Math.cos(rotation) * dx + Math.sin(rotation) * dz;
  const layoutZ = -Math.sin(rotation) * dx + Math.cos(rotation) * dz;
  const centeredX = layoutX - element.dimensions.width / 2;
  const centeredZ = layoutZ - element.dimensions.height / 2;

  const facing = resolveEquipment3DPortFacing(element).rotationY;
  const localX = Math.cos(facing) * centeredX - Math.sin(facing) * centeredZ;
  const localZ = Math.sin(facing) * centeredX + Math.cos(facing) * centeredZ;
  return [localX, localY, localZ];
}
